import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GameAssetsProvider } from '../../XXX/context/GameAssetContext';
import PathfindingMap, { PathfindingMapHandle } from './PathfindingMap';
import MapChampionPanel from './MapChampionPanel';
import MapChampionCards from './MapChampionCards';
import {
  MapChampion,
  Ward,
  Waypoint,
  PathSegment,
  getChampionMS,
  FLASH_RANGE_GRID,
} from './mapTypes';
import './MapViewerPage.css';

type LaneChoice = 'top' | 'mid' | 'bot';

/* ══════════════════ Constants ══════════════════ */
const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/riftmap`;

/* ══════════════════ Component ══════════════════ */
const MapViewerPage: React.FC = () => {
  /* ── Shared state ── */
  const [champions, setChampions] = useState<MapChampion[]>([]);
  const [selectedChampIdx, setSelectedChampIdx] = useState<number | null>(null);
  const [flashMode, setFlashMode] = useState(false);

  /* ── Ward state ── */
  const [wards, setWards] = useState<Ward[]>([]);
  const [wardTeam, setWardTeam] = useState<'blue' | 'red'>('red');
  const [wardMode, setWardMode] = useState<{
    type: 'stealth' | 'control';
    team: 'blue' | 'red';
  } | null>(null);
  const [showVision, setShowVision] = useState(true);
  const wardIdRef = useRef(0);

  /* ── Map status (reported by PathfindingMap) ── */
  const [mapStatus, setMapStatus] = useState({
    zoom: 1,
    cursorGx: -1,
    cursorGy: -1,
  });

  /* ── Ref for latest champions (used inside callbacks / setTimeout) ── */
  const championsRef = useRef(champions);
  useEffect(() => {
    championsRef.current = champions;
  });

  /* ── Map imperative handle ── */
  const mapRef = useRef<PathfindingMapHandle>(null);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'f' || e.key === 'F') {
        setFlashMode((f) => !f);
        setWardMode(null); // cancel ward mode when toggling flash
      }
      if (e.key === 'Escape') {
        setFlashMode(false);
        setWardMode(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  /* ═══════════ Core: calculate segments for a set of waypoints ═══════════ */
  const calculatePathSegments = useCallback(
    async (
      champIdx: number,
      startPos: { gx: number; gy: number },
      waypoints: Waypoint[],
      speed: number,
    ) => {
      const segments: PathSegment[] = [];

      for (let i = 0; i < waypoints.length; i++) {
        const start = i === 0 ? startPos : waypoints[i - 1];
        const end = waypoints[i];
        const isFlash = end.isFlash || false;

        if (isFlash) {
          segments.push({
            points: [
              { x: start.gx, y: start.gy },
              { x: end.gx, y: end.gy },
            ],
            isFlash: true,
            travelTimeSeconds: 0,
          });
        } else {
          try {
            const res = await fetch(`${API_BASE}/pathfind`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                startX: start.gx,
                startY: start.gy,
                endX: end.gx,
                endY: end.gy,
                movementSpeed: speed,
              }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            segments.push({
              points: data.path,
              isFlash: false,
              travelTimeSeconds: data.travelTimeSeconds,
            });
          } catch (err: any) {
            console.error(`Segment ${i} error:`, err.message);
            // straight-line fallback
            segments.push({
              points: [
                { x: start.gx, y: start.gy },
                { x: end.gx, y: end.gy },
              ],
              isFlash: false,
              travelTimeSeconds: 0,
            });
          }
        }
      }

      const totalTime = segments.reduce(
        (sum, s) => sum + s.travelTimeSeconds,
        0,
      );
      setChampions((prev) => {
        const u = [...prev];
        if (!u[champIdx]) return prev;
        u[champIdx] = {
          ...u[champIdx],
          pathSegments: segments,
          travelTimeSeconds: totalTime,
          pathLoading: false,
        };
        return u;
      });
    },
    [],
  );

  /* ═══════════ Add waypoint ═══════════ */
  const addWaypoint = useCallback(
    async (
      champIdx: number,
      pos: { gx: number; gy: number },
      isFlash: boolean,
    ) => {
      const mc = championsRef.current[champIdx];
      if (!mc?.gridPosition) return;

      // Determine the "previous" point (last waypoint or champion pos)
      const prevPoint =
        mc.waypoints.length > 0
          ? mc.waypoints[mc.waypoints.length - 1]
          : mc.gridPosition;

      let wp: Waypoint = { ...pos, isFlash: isFlash || undefined };

      // Flash: clamp to max range, then resolve destination (50% wall rule)
      if (isFlash) {
        const dx = pos.gx - prevPoint.gx;
        const dy = pos.gy - prevPoint.gy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > FLASH_RANGE_GRID) {
          const scale = FLASH_RANGE_GRID / dist;
          wp = {
            gx: Math.round(prevPoint.gx + dx * scale),
            gy: Math.round(prevPoint.gy + dy * scale),
            isFlash: true,
          };
        }

        // Ask backend to resolve the landing position (handles wall 50% rule)
        try {
          const resolveRes = await fetch(`${API_BASE}/flash-resolve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              startX: prevPoint.gx,
              startY: prevPoint.gy,
              targetX: wp.gx,
              targetY: wp.gy,
            }),
          });
          if (resolveRes.ok) {
            const resolved: { gx: number; gy: number } = await resolveRes.json();
            wp = { gx: resolved.gx, gy: resolved.gy, isFlash: true };
          }
        } catch (err) {
          console.error('Flash resolve error:', err);
        }
      }

      const newWaypoints = [...mc.waypoints, wp];

      // Mark loading + add waypoint
      setChampions((prev) => {
        const u = [...prev];
        if (!u[champIdx]) return prev;
        u[champIdx] = {
          ...u[champIdx],
          waypoints: newWaypoints,
          pathLoading: true,
        };
        return u;
      });

      // Only calculate the NEW segment (not full recalc)
      if (isFlash) {
        const segment: PathSegment = {
          points: [
            { x: prevPoint.gx, y: prevPoint.gy },
            { x: wp.gx, y: wp.gy },
          ],
          isFlash: true,
          travelTimeSeconds: 0,
        };
        setChampions((prev) => {
          const u = [...prev];
          if (!u[champIdx]) return prev;
          const newSegs = [...u[champIdx].pathSegments, segment];
          const total = newSegs.reduce((s, seg) => s + seg.travelTimeSeconds, 0);
          u[champIdx] = {
            ...u[champIdx],
            pathSegments: newSegs,
            travelTimeSeconds: total,
            pathLoading: false,
          };
          return u;
        });
        // Auto-deactivate flash mode after placing
        setFlashMode(false);
      } else {
        try {
          const speed = getChampionMS(mc);
          const res = await fetch(`${API_BASE}/pathfind`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              startX: prevPoint.gx,
              startY: prevPoint.gy,
              endX: wp.gx,
              endY: wp.gy,
              movementSpeed: speed,
            }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const segment: PathSegment = {
            points: data.path,
            isFlash: false,
            travelTimeSeconds: data.travelTimeSeconds,
          };
          setChampions((prev) => {
            const u = [...prev];
            if (!u[champIdx]) return prev;
            const newSegs = [...u[champIdx].pathSegments, segment];
            const total = newSegs.reduce(
              (s, seg) => s + seg.travelTimeSeconds,
              0,
            );
            u[champIdx] = {
              ...u[champIdx],
              pathSegments: newSegs,
              travelTimeSeconds: total,
              pathLoading: false,
            };
            return u;
          });
        } catch (err: any) {
          console.error('Path error:', err.message);
          setChampions((prev) => {
            const u = [...prev];
            if (!u[champIdx]) return prev;
            u[champIdx] = { ...u[champIdx], pathLoading: false };
            return u;
          });
        }
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /* ═══════════ Remove a single waypoint ═══════════ */
  const removeWaypoint = useCallback(
    async (champIdx: number, wpIdx: number) => {
      const mc = championsRef.current[champIdx];
      if (!mc?.gridPosition) return;

      const newWaypoints = mc.waypoints.filter((_, i) => i !== wpIdx);

      if (newWaypoints.length === 0) {
        // No waypoints left → clear path entirely
        setChampions((prev) => {
          const u = [...prev];
          if (!u[champIdx]) return prev;
          u[champIdx] = {
            ...u[champIdx],
            waypoints: [],
            pathSegments: [],
            travelTimeSeconds: null,
            pathLoading: false,
          };
          return u;
        });
        return;
      }

      setChampions((prev) => {
        const u = [...prev];
        if (!u[champIdx]) return prev;
        u[champIdx] = {
          ...u[champIdx],
          waypoints: newWaypoints,
          pathLoading: true,
          pathSegments: [],
          travelTimeSeconds: null,
        };
        return u;
      });

      await calculatePathSegments(
        champIdx,
        mc.gridPosition,
        newWaypoints,
        getChampionMS(mc),
      );
    },
    [calculatePathSegments],
  );

  /* ═══════════ Recalculate all segments (drag-end / MS change) ═══════════ */
  const recalculateFullPath = useCallback(
    async (champIdx: number) => {
      const mc = championsRef.current[champIdx];
      if (!mc?.gridPosition || mc.waypoints.length === 0) return;

      setChampions((prev) => {
        const u = [...prev];
        if (!u[champIdx]) return prev;
        u[champIdx] = { ...u[champIdx], pathLoading: true };
        return u;
      });

      await calculatePathSegments(
        champIdx,
        mc.gridPosition,
        mc.waypoints,
        getChampionMS(mc),
      );
    },
    [calculatePathSegments],
  );

  /* ═══════════ Ward callbacks ═══════════ */
  const placeWard = useCallback(
    (gx: number, gy: number) => {
      if (!wardMode) return;
      const id = `w-${wardIdRef.current++}`;
      setWards((prev) => [...prev, { id, gx, gy, type: wardMode.type, team: wardMode.team }]);
    },
    [wardMode],
  );

  const removeWard = useCallback((wardId: string) => {
    setWards((prev) => prev.filter((w) => w.id !== wardId));
  }, []);

  const clearWards = useCallback(() => setWards([]), []);

  const toggleWardMode = useCallback(
    (type: 'stealth' | 'control') => {
      setWardMode((prev) => {
        if (prev && prev.type === type && prev.team === wardTeam) return null; // toggle off
        return { type, team: wardTeam };
      });
      setFlashMode(false); // cancel flash when entering ward mode
    },
    [wardTeam],
  );

  /* ═══════════ Clear helpers ═══════════ */
  const clearAllPaths = useCallback(() => {
    setChampions((prev) =>
      prev.map((mc) => ({
        ...mc,
        waypoints: [],
        pathSegments: [],
        travelTimeSeconds: null,
        pathLoading: false,
      })),
    );
  }, []);

  /* ═══════════ Champion card update ═══════════ */
  const handleChampionCardUpdate = useCallback(
    (updatedChampion: any) => {
      setChampions((prev) =>
        prev.map((mc, idx) => {
          if (mc.champion.id !== updatedChampion.id) return mc;
          const oldMS = getChampionMS(mc);
          const updated = { ...mc, champion: updatedChampion };
          const newMS = getChampionMS(updated);
          // If MS changed and champion has waypoints → recalculate path
          if (
            oldMS !== newMS &&
            updated.waypoints.length > 0 &&
            updated.gridPosition
          ) {
            setTimeout(() => recalculateFullPath(idx), 0);
          }
          return updated;
        }),
      );
    },
    [recalculateFullPath],
  );

  /* ═══════════ Lane-path export ═══════════ */
  const [exportLane, setExportLane] = useState<LaneChoice>('top');
  const [exportStatus, setExportStatus] = useState<string | null>(null);

  const exportAsLanePath = useCallback(async () => {
    if (selectedChampIdx === null) {
      setExportStatus('Select a champion first');
      return;
    }
    const champ = championsRef.current[selectedChampIdx];
    if (!champ || champ.pathSegments.length === 0) {
      setExportStatus('Champion has no path — add waypoints first');
      return;
    }

    // Flatten all path segments into one ordered waypoint array
    const allPoints: { x: number; y: number }[] = [];
    if (champ.gridPosition) {
      allPoints.push({ x: champ.gridPosition.gx, y: champ.gridPosition.gy });
    }
    for (const seg of champ.pathSegments) {
      const start = allPoints.length === 0 ? 0 : 1; // skip duplicate joint
      for (let i = start; i < seg.points.length; i++) {
        allPoints.push(seg.points[i]);
      }
    }

    if (allPoints.length < 2) {
      setExportStatus('Path too short');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/lane-paths`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lane: exportLane, waypoints: allPoints }),
      });
      const data = await res.json();
      if (data.success) {
        setExportStatus(`✓ Saved ${exportLane} lane (${data.waypointCount} pts)`);
      } else {
        setExportStatus(`✗ ${data.error}`);
      }
    } catch (err: any) {
      setExportStatus(`✗ ${err.message}`);
    }
    // Clear status after a few seconds
    setTimeout(() => setExportStatus(null), 4000);
  }, [selectedChampIdx, exportLane]);

  /* ═══════════ Derived ═══════════ */
  const selectedChamp =
    selectedChampIdx !== null ? champions[selectedChampIdx] : null;

  /* ═══════════ Render ═══════════ */
  return (
    <GameAssetsProvider>
      {/* ─── Map Section ─── */}
      <section className="mvp-section mvp-map-section">
        <div className="mvp-toolbar">
          <div className="mvp-toolbar-row">
            <div className="mvp-hint">
              <b>Right-click</b> to add waypoint &middot;{' '}
              <b>F</b> or <b>⚡&nbsp;Flash</b> to blink through walls &middot;{' '}
              <b>Left-click</b> waypoint to remove &middot;{' '}
              Drag champion to move
            </div>
            <div className="mvp-btnset">
              <button
                className={`mvp-btn${flashMode ? ' mvp-btn-active' : ''}`}
                onClick={() => { setFlashMode((f) => !f); setWardMode(null); }}
                title="Toggle Flash — next right-click will blink through walls"
              >
                ⚡ Flash
              </button>
              <button className="mvp-btn" onClick={clearAllPaths}>
                Clear Paths
              </button>
              <button
                className="mvp-btn"
                onClick={() => mapRef.current?.resetView()}
              >
                Reset View
              </button>
            </div>
          </div>

          {/* ── Ward toolbar row ── */}
          <div className="mvp-toolbar-row">
            <div className="mvp-hint">
              <b>Wards</b>: click map to place · click ward to remove · ESC to cancel
            </div>
            <div className="mvp-btnset">
              <button
                className={`mvp-btn mvp-btn-sm${wardTeam === 'blue' ? ' mvp-btn-team-blue' : ''}`}
                onClick={() => { setWardTeam('blue'); setWardMode((m) => m ? { ...m, team: 'blue' } : null); }}
              >
                Blue
              </button>
              <button
                className={`mvp-btn mvp-btn-sm${wardTeam === 'red' ? ' mvp-btn-team-red' : ''}`}
                onClick={() => { setWardTeam('red'); setWardMode((m) => m ? { ...m, team: 'red' } : null); }}
              >
                Red
              </button>

              <span className="mvp-sep" />

              <button
                className={`mvp-btn${wardMode?.type === 'stealth' ? ' mvp-btn-ward-active' : ''}`}
                onClick={() => toggleWardMode('stealth')}
                title="Place stealth ward (click map)"
              >
                ◉ Ward
              </button>
              <button
                className={`mvp-btn${wardMode?.type === 'control' ? ' mvp-btn-ward-active' : ''}`}
                onClick={() => toggleWardMode('control')}
                title="Place control ward — suppresses enemy stealth wards"
              >
                👁 Control
              </button>

              <span className="mvp-sep" />

              <button className="mvp-btn" onClick={clearWards}>
                Clear Wards
              </button>
              <label className="mvp-check">
                <input
                  type="checkbox"
                  checked={showVision}
                  onChange={() => setShowVision((v) => !v)}
                />
                Vision
              </label>
            </div>
          </div>

          {/* ── Lane-path export row ── */}
          <div className="mvp-toolbar-row">
            <div className="mvp-hint">
              <b>Lane Path</b>: place a champion, add waypoints from base → base,
              then export as a minion lane route
            </div>
            <div className="mvp-btnset">
              <select
                className="mvp-select"
                value={exportLane}
                onChange={(e) => setExportLane(e.target.value as LaneChoice)}
              >
                <option value="top">Top</option>
                <option value="mid">Mid</option>
                <option value="bot">Bot</option>
              </select>
              <button
                className="mvp-btn"
                onClick={exportAsLanePath}
                disabled={selectedChampIdx === null}
                title="Save selected champion's path as a minion lane route"
              >
                Export as Lane Path
              </button>
              {exportStatus && (
                <span className="mvp-export-status">{exportStatus}</span>
              )}
            </div>
          </div>
        </div>

        <div className="mvp-map-wrap">
          <PathfindingMap
            ref={mapRef}
            champions={champions}
            selectedChampIdx={selectedChampIdx}
            flashMode={flashMode}
            onChampionsChange={setChampions}
            onSelectChampion={setSelectedChampIdx}
            onAddWaypoint={addWaypoint}
            onRemoveWaypoint={removeWaypoint}
            onRecalculateFullPath={recalculateFullPath}
            onStatusChange={setMapStatus}
            wards={wards}
            wardMode={wardMode}
            showVision={showVision}
            onPlaceWard={placeWard}
            onRemoveWard={removeWard}
          />
        </div>

        <div className="mvp-status">
          <span>
            Zoom: <b>{mapStatus.zoom.toFixed(1)}x</b>
          </span>
          <span>
            Grid:{' '}
            <b>
              ({mapStatus.cursorGx >= 0 ? mapStatus.cursorGx : '—'},{' '}
              {mapStatus.cursorGy >= 0 ? mapStatus.cursorGy : '—'})
            </b>
          </span>
          {selectedChamp && (
            <span>
              Selected:{' '}
              <b style={{ color: selectedChamp.color }}>
                {selectedChamp.champion.name}
              </b>
              {selectedChamp.gridPosition &&
                ` @ (${selectedChamp.gridPosition.gx}, ${selectedChamp.gridPosition.gy})`}
              {' — '}
              {Math.round(getChampionMS(selectedChamp))} MS
            </span>
          )}
          <span>
            Champions: <b>{champions.length}</b>
          </span>
          {wards.length > 0 && (
            <span>
              Wards: <b>{wards.length}</b>
            </span>
          )}
          {flashMode && (
            <span style={{ color: '#ffd700' }}>⚡ FLASH MODE</span>
          )}
          {wardMode && (
            <span style={{ color: wardMode.team === 'blue' ? '#4a9eff' : '#ff6b6b' }}>
              🔲 {wardMode.team.toUpperCase()} {wardMode.type.toUpperCase()} WARD
            </span>
          )}
        </div>
      </section>

      {/* ─── Champion Panel (add / select) + Cards ─── */}
      <section className="mvp-section">
        <MapChampionPanel
          champions={champions}
          onChampionsChange={setChampions}
          selectedChampionIdx={selectedChampIdx}
          onSelectChampion={setSelectedChampIdx}
        />
        <MapChampionCards
          champions={champions}
          onChampionUpdate={handleChampionCardUpdate}
        />
      </section>
    </GameAssetsProvider>
  );
};

export default MapViewerPage;
